import { useState, useMemo } from "react";
import { useAuth } from "wasp/client/auth";
import { useNavigate, Link } from "react-router-dom";
import { useQuery } from "wasp/client/operations";
import { getSections } from "wasp/client/operations";
import { Filter, Plus, Upload } from "lucide-react";
import LoadingSpinner from "./layout/LoadingSpinner";
import { EnhancedActionButton } from "../components/ui/enhanced-page-layout";
import { TableFilterLayout } from "../components/ui/table-filter-layout";
import { BulkUploadDialog } from "./bulk-upload/components/BulkUploadDialog";
import { sectionConfig } from "./bulk-upload/configs";
import { useBulkUploadEnabled } from "./hooks/useBulkUploadEnabled";

export default function SectionsPage() {
  const { data: user } = useAuth();
  const navigate = useNavigate();

  if (!user) {
    return <div>Loading...</div>;
  }

  const { data: sections = [], isLoading, error, refetch } = useQuery(getSections);
  const { isEnabled: isBulkUploadEnabled } = useBulkUploadEnabled();

  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [filterBase, setFilterBase] = useState("all");
  const [filterArchived, setFilterArchived] = useState("all");

  const filteredData = useMemo(() => {
    return (sections as any[]).filter((section: any) => {
      if (searchTerm && !section.section_key.toLowerCase().includes(searchTerm.toLowerCase())) {
        return false;
      }
      if (filterBase === "base" && !section.isBase) return false;
      if (filterBase === "type-specific" && !section.isTypeSpecific) return false;
      if (filterArchived === "archived" && !section.archived) return false;
      if (filterArchived === "active" && section.archived) return false;
      return true;
    });
  }, [sections, searchTerm, filterBase, filterArchived]);

  const hasData = (sections as any[]).length > 0;

  return (
    <>
      <div className="w-full">
        {/* Fixed Header */}
        <div className="fixed top-[var(--header-height)] left-[var(--sidebar-width)] right-0 z-30 bg-white dark:bg-sidebar-active transition-colors border-b border-gray-200 dark:border-gray-700">
          <div className="px-4 py-3">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Organization Sections</h1>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                  Configure sections and their hierarchy ({filteredData.length} total)
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowFilters(f => !f)}
                  className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md border transition-colors ${
                    showFilters
                      ? 'bg-blue-50 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-600 dark:text-blue-300'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 dark:bg-transparent dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800'
                  }`}
                >
                  <Filter className="w-4 h-4" />
                  Filter
                </button>
                {isBulkUploadEnabled && (
                  <EnhancedActionButton
                    onClick={() => setShowBulkUpload(true)}
                    icon={Upload}
                    label="Bulk Upload"
                    variant="secondary"
                  />
                )}
                <EnhancedActionButton
                  onClick={() => navigate('/admin/sections/new')}
                  icon={Plus}
                  label="Create Section"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Spacer to push content below fixed header */}
        <div className="h-20" />

        {/* Table Header with Filters */}
        {hasData && (
          <TableFilterLayout
            showFilters={showFilters}
            columns={[
              { title: 'Section Key', span: 4 },
              { title: 'Parent Section', span: 3 },
              { title: 'Order', span: 1 },
              { title: 'Type', span: 2 },
              { title: 'Status', span: 2 }
            ]}
            filters={[
              {
                span: 4,
                content: (
                  <input
                      type="text"
                      placeholder="Search section key..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                )
              },
              { span: 3, content: <div className="h-10" /> },
              { span: 1, content: <div className="h-10" /> },
              {
                span: 2,
                content: (
                  <select
                    value={filterBase}
                    onChange={(e) => setFilterBase(e.target.value)}
                    className="appearance-none w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">All Types</option>
                    <option value="base">Base Only</option>
                    <option value="type-specific">Type-Specific Only</option>
                  </select>
                )
              },
              {
                span: 2,
                content: (
                  <select
                    value={filterArchived}
                    onChange={(e) => setFilterArchived(e.target.value)}
                    className="appearance-none w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">All Status</option>
                    <option value="active">Active Only</option>
                    <option value="archived">Archived Only</option>
                  </select>
                )
              }
            ]}
          />
        )}

        {/* Content */}
        <div className="w-full">
            {isLoading && <LoadingSpinner />}
            {error && (
              <div className="p-4 text-center text-red-600">
                Error loading sections: {error.message}
              </div>
            )}

            {!isLoading && !error && filteredData.length === 0 && (
              <div className="p-8 text-center text-gray-500">
                {/* Icon removed */}
                <p className="text-lg font-medium">No sections found</p>
                <p className="text-sm mt-2">
                  {searchTerm || filterBase !== 'all' || filterArchived !== 'all'
                    ? "Try adjusting your filters"
                    : "Click 'Create Section' to add your first section"}
                </p>
              </div>
            )}

            {filteredData.length > 0 && (
              <>
                {filteredData.map((section: any) => (
                  <div
                    key={section.id}
                    className="px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-sidebar-hover transition-colors grid grid-cols-12 gap-4 items-center border-b border-gray-200 dark:border-gray-700"
                  >
                    <div className="col-span-4 overflow-hidden min-w-0">
                      <Link
                        to={`/admin/sections/${section.id}`}
                        className="flex items-center gap-2 hover:text-blue-600 transition-colors"
                      >
                        {/* Icon removed */}
                        <span className="text-sm font-medium text-gray-900 dark:text-white hover:text-blue-600">
                          {section.section_key}
                        </span>
                      </Link>
                    </div>
                    <div className="col-span-3 overflow-hidden min-w-0">
                      {section.parentSection ? (
                        <Link
                          to={`/admin/sections/${section.parentSection.id}`}
                          className="text-sm text-gray-600 dark:text-gray-300 hover:text-blue-600 transition-colors"
                        >
                          {section.parentSection.section_key}
                        </Link>
                      ) : (
                        <span className="text-sm text-gray-600 dark:text-gray-300 truncate block">-</span>
                      )}
                    </div>
                    <div className="col-span-1 overflow-hidden min-w-0">
                      <span className="text-sm text-gray-600 dark:text-gray-300 truncate block">
                        {section.order}
                      </span>
                    </div>
                    <div className="col-span-2 overflow-hidden min-w-0">
                      <span className="text-xs px-2 py-1 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
                        {section.isBase ? 'Base' : section.isTypeSpecific ? 'Type-Specific' : 'Standard'}
                      </span>
                    </div>
                    <div className="col-span-2 overflow-hidden min-w-0">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        section.archived
                          ? 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400'
                          : 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400'
                      }`}>
                        {section.archived ? 'Archived' : 'Active'}
                      </span>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
      </div>
      <BulkUploadDialog
        open={showBulkUpload}
        onOpenChange={setShowBulkUpload}
        config={sectionConfig}
        onComplete={() => refetch()}
      />
    </>
  );
}



